import React, { useCallback, useRef } from 'react';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { blueButtonClass } from 'src/components/common/common';
import { InspectTable_KSK2021_R01 } from './InspectTable_KSK2021_R01';
import { useKSK2021_R01Store } from './Store_KSK2021_R01';
import { AgGridReact } from 'ag-grid-react';
import { KSK2021_R01HeaderDto, KSK2021_R01InspectDto } from './Dto_KSK2021_R01';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { validateEditBadQtyAgainstInspectList } from './inspectBadQtyUtil_KSK2021_R01';

interface Props {
	defaultKSK2021_R01HeaderRow: KSK2021_R01HeaderDto;
    inspectGridRef: React.RefObject<AgGridReact<KSK2021_R01InspectDto> | null>;
	defaultKSK2021_R01InspectRow: KSK2021_R01InspectDto;
    handleDeleteClick: () => void;
}

export const Modal_KSK2021_R01 = ({
	defaultKSK2021_R01HeaderRow,
    inspectGridRef,
    defaultKSK2021_R01InspectRow,
    handleDeleteClick,
}: Props) => {
    const { 
		modalVisible, setModalVisible,
		editKSK2021_R01, setEditKSK2021_R01,
		KSK2021_R01InspectList,
		deletedKSK2021_R01InspectList,
		insertKSK2021_R01InspectList, 
		removeKSK2021_R01InspectList,
	 } = useKSK2021_R01Store();

    const modalRef = useRef<HTMLDivElement>(null);
    const modalInfoRef = useRef<HTMLDivElement>(null);
    const isResizing_modal = useRef(false);

    const RESIZER_MODAL = {
        code: 'KSK2021_R01_modal',
        type: 'width',
        storageKey: 'KSK2021_R01-modal-split-width',
        min: 5,
        max: 80,
        default: 50
    } as const;

    const {
        splitValue: splitValue_modal,
        handleMouseDown: handleMouseDown_modal,
    } = useMouseContent({
        resizer: RESIZER_MODAL,
        isResizing: isResizing_modal,
        containerRef: modalRef,
        infoRef: modalRef
    });

	const handleBadQtyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setEditKSK2021_R01({
			...defaultKSK2021_R01HeaderRow,
			...editKSK2021_R01,
			[name]: value,
			badQty: Number(value) || 0
		});
	}, [editKSK2021_R01, defaultKSK2021_R01HeaderRow, setEditKSK2021_R01]);

	const handleRemoveLastRow = useCallback(() => {
		if (KSK2021_R01InspectList.length > 1) {
			const lastItem = KSK2021_R01InspectList[KSK2021_R01InspectList.length - 1];
			removeKSK2021_R01InspectList(lastItem.valueIndex);
		}else{
			showWarning('마지막 행은 삭제할 수 없습니다.');
		}
	}, [KSK2021_R01InspectList, removeKSK2021_R01InspectList]);

	const validateInspectBadQty = useCallback(() => {
		const deletedInspectListToNumber = deletedKSK2021_R01InspectList.map(value => Number(value));
		const result = validateEditBadQtyAgainstInspectList(
			editKSK2021_R01?.badQty || 0,
			KSK2021_R01InspectList,
			deletedInspectListToNumber,
		);

		if (!result.valid) {
			showWarning(result.message!);
			return false;
		}
		return true;
	}, [editKSK2021_R01?.badQty, deletedKSK2021_R01InspectList, KSK2021_R01InspectList]);

	const handleConfirmClick = useCallback(() => {
		if (!validateInspectBadQty()) return;
		setModalVisible(false);
	}, [validateInspectBadQty, setModalVisible]);

	const handleCancelClick = useCallback(() => {
		if (!validateInspectBadQty()) return;
		setModalVisible(false);
	}, [validateInspectBadQty, setModalVisible]);

    if (!modalVisible) return null;

    return (
        <ModalWrapper
            size={'qmd'}
            defaultWidth={600}
            overlay={true}
            onOverlayClick={handleCancelClick}
            onEscKeyDown={handleCancelClick}
        >
            <div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
                <p className="font-bold">
                    불량 내용 등록
                </p>
            </div>
            <div className="w-full h-[10%] flex items-center px-[24px] gap-[8px]">
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleConfirmClick}
                    disabled={false}
                >
                    확인
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleCancelClick}
                    disabled={false}
                >
                    취소
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleDeleteClick}
                    disabled={false}
                >
                    삭제
                </button>
				<button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={() => insertKSK2021_R01InspectList([defaultKSK2021_R01InspectRow])}
                    disabled={false}
                >
                    행+
                </button>
				<button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleRemoveLastRow}
                    disabled={false}
                >
                    행-
                </button>
				<InputForm title="불량수량" value={editKSK2021_R01?.badQty || 0}
						editable={true}
						name="badQty"
						onChange={handleBadQtyChange}
						dragSelectAll={true}
						wrapperOption={{
							wrapperStyle: "w-[230px] min-w-[230px] mr-[10px] mb-[4px] h-[29px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-[130px] min-w-[130px]",
						}}
					/>
            </div>
            <div className="w-full h-[75%] flex px-[24px] pt-[2px] pb-[12px] gap-[8px]" ref={modalRef}>
                <div className="h-full" style={{ width: `100%` }} ref={modalInfoRef}>
                    <InspectTable_KSK2021_R01 gridRef={inspectGridRef} defaultKSK2021_R01InspectRow={defaultKSK2021_R01InspectRow} />
                </div>
            </div>
        </ModalWrapper>
    );
};
